import { StatusChipColor } from "components/StatusChip";
import { getWastePipelineConfig, isWastePipelineName } from "encamp-shared/src/pipelines";
import { WastePipelineApprovalStatus } from "generated-graphql/graphql";
import { prettyPrintDateTime } from "util/dates";

export function getIngestionAlertSeverity(
  status: string
): "success" | "error" | "warning" | "info" {
  if (status === "SUCCEEDED") return "success";
  if (status.startsWith("FAILED")) return "error";
  if (status === "QUEUED") return "warning";
  return "info";
}

export function getIngestionStatusColor(
  status: string | null | undefined
): "success" | "error" | "warning" | "info" | "default" {
  if (!status) return "default";
  if (status === "SUCCEEDED") return "success";
  if (status.startsWith("FAILED")) return "error";
  if (status === "RUNNING" || status === "DISPATCHED") return "info";
  if (status === "QUEUED") return "warning";
  return "default";
}

/**
 * The run's state for display. Rows created before runs were linked to their
 * job have no runJob, so a completed run is recognized by its lastRunAt and
 * anything else is unknown.
 */
export function getRunStatusChip(row: {
  lastRunAt?: string | null;
  runJob?: { status: string } | null;
}): { label: string; color: StatusChipColor } {
  const status = row.runJob?.status;
  if (status) return { label: status, color: getIngestionStatusColor(status) };
  if (row.lastRunAt) return { label: "SUCCEEDED", color: "success" };
  return { label: "UNKNOWN", color: "default" };
}

// NOT_REVIEWED is a warning (ingestion is blocked until someone acts) while
// NO_PIPELINE is neutral: there is nothing to approve. Labels come from
// WASTE_PIPELINE_APPROVAL_LABELS in encamp-shared.
export const INGEST_APPROVAL_CHIP_COLORS: Record<WastePipelineApprovalStatus, StatusChipColor> = {
  [WastePipelineApprovalStatus.Approved]: "success",
  [WastePipelineApprovalStatus.NotReviewed]: "warning",
  [WastePipelineApprovalStatus.NoPipeline]: "default",
};

/** The vendor a run's pipeline parses, for copy; falls back to the run's name. */
export function vendorLabelFor(pipelineName: string): string {
  return isWastePipelineName(pipelineName)
    ? getWastePipelineConfig(pipelineName).vendor
    : pipelineName;
}

/** The routine per-run line of the ingest confirmation: what this click loads. */
export function ingestConfirmInfo(
  pipeline: { ingestedAt?: string | null; tenant?: { name: string } | null } | null | undefined,
  matchedRowCount: number
): string {
  if (pipeline?.ingestedAt) {
    return `This pipeline was previously ingested on ${prettyPrintDateTime(pipeline.ingestedAt)}. Re-ingesting will overwrite existing data.`;
  }
  const tenantName = pipeline?.tenant?.name ?? "this organization";
  return `This will import ${matchedRowCount.toLocaleString()} matched rows into the system for ${tenantName}.`;
}
